import { useReportsFindOne } from '@/api/WhisApi/generated';
import { EmptyStateView } from '@/components/EmptyStateView/EmptyStateView';
import { LoadingView } from '@/components/LoadingView/LoadingView';
import { TabScreenContainer } from '@/components/TabScreenContainer';
import { showToast } from '@/components/Toast/toast.service';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { useIsWeb } from '@/hooks/useIsWeb';
import { copyToClipboard, openShareSheet } from '@/utils/share.utils';
import { Button } from '@components/Button/Button';
import { useBreakpoint } from '@hooks/useBreakpoint';
import ReportDetail from '@screens/ReportDetail/components/ReportDetail';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback } from 'react';
import { ScrollView } from 'react-native';
import { getTokens, XStack, YStack } from 'tamagui';
import { ReportDetailFooterActions } from './components/ReportDetailFooterActions';
import { getReportShareUrl } from './reportDetailShare.utils';

export default function ReportDetailScreen(): React.ReactNode {
  const { t } = useAppTranslation();
  const uiTokens = getTokens();
  const router = useRouter();
  const { reportId } = useLocalSearchParams<{ reportId?: string }>();
  const { isSmallScreen } = useBreakpoint();
  const isWeb = useIsWeb();

  const handleBack = useCallback((): void => {
    if (router.canGoBack()) {
      router.back();
      return;
    }

    router.replace('/(tabs)/reports');
  }, [router]);

  const handleShare = async (): Promise<void> => {
    if (!reportId) {
      return;
    }

    const shareUrl = getReportShareUrl(reportId);

    await openShareSheet({
      message: shareUrl,
      ...(isWeb ? {} : { title: t('reportDetail.share.title') }),
    });
  };

  const handleCopyShareUrl = async (): Promise<void> => {
    if (!reportId) {
      return;
    }

    const shareUrl = getReportShareUrl(reportId);

    try {
      await copyToClipboard(shareUrl);
      showToast({
        type: 'success',
        title: t('reportDetail.toasts.copyShareUrlSuccess'),
      });
    } catch {
      showToast({
        type: 'error',
        title: t('reportDetail.toasts.copyShareUrlError'),
      });
    }
  };

  const { data: report, isPending } = useReportsFindOne(reportId ?? '', {
    query: { enabled: Boolean(reportId) },
  });

  if (!reportId) {
    return (
      <TabScreenContainer>
        <EmptyStateView title={t('noData')} />
      </TabScreenContainer>
    );
  }

  if (isPending) {
    return (
      <TabScreenContainer>
        <LoadingView />
      </TabScreenContainer>
    );
  }

  if (!report) {
    return (
      <TabScreenContainer>
        <EmptyStateView title={t('noData')} />
      </TabScreenContainer>
    );
  }

  return (
    <TabScreenContainer>
      <YStack flex={1}>
        <ScrollView
          style={{ flex: 1 }}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{
            flexGrow: 1,
            paddingVertical: uiTokens.space.$true.val,
          }}
        >
          <YStack
            width="100%"
            maxWidth="$compactPageWidth"
            alignSelf="center"
            gap="$true"
          >
            {!isSmallScreen && (
              <XStack alignItems="center">
                <Button
                  title={t('buttons.back')}
                  variant="outlined"
                  onPress={handleBack}
                />
              </XStack>
            )}
            <ReportDetail
              report={report}
              onShare={() => void handleShare()}
              onCopyShareUrl={() => void handleCopyShareUrl()}
            />
          </YStack>
        </ScrollView>

        <ReportDetailFooterActions reportId={reportId} report={report} />
      </YStack>
    </TabScreenContainer>
  );
}
